import {
    getCurrentTimestamp,
    getCurrentUser,
    getCustomFormattedDateBeginningByYear, getFormattedFullFrDateWithSeconds
} from "../components/CommonFunctions";
import {sendFarmCommandEvent} from "./socketEventsService";
import {postFarmEvent} from "../api/apiFarmCommand";

/* interval between 2 influxdb requests (in seconds) - for data list */
export const INFLUXDB_LIST_REQUEST_INTERVAL = 600;

/* interval between 2 printers status requests (in seconds) */
export const PRINTERS_STATUS_REQUEST_INTERVAL = 60;

/* time limit to wait to validate command sent to smart farm (in seconds) */
export const COMMAND_TIMEOUT_LIMIT = 30;

export const CELL_TYPES = {PRINTER: 0, MATERIALS: 1, POST_TREATMENT: 2, BALANCE: 3};

/* time limit beyond which there is a connection error  */
export const DATA_TIMEOUT_LIMIT = 35;

/* On/off states */
export const ON_OFF_STATES = {
    OFF: 0,
    ON: 1
};

/* LEDs colors */
export const LED_COLORS = {
    OFF: {index: 0, color: '#000000'},
    WHITE: {index: 7, color: '#e5e5e5'}, // Status: stand by
    RED: {index: 2, color: '#f04040'},
    GREEN: {index: 3, color: '#40f040'},
    BLUE_HANDDDLE: {index: 5, color: '#00cac0'},
    ORANGE: {index: 4, color: '#ffa500'}, // Status: printing
    BLUE: {index: 6, color: '#4040f0'}
};

export const LED_MODES = {
    STATIC: 0,
    BOUNCE: 1
};

/* Door states */
export const DOOR_STATES = {
    OPEN: 0,
    CLOSED: 1,
    LEFT_OPEN: 2,
    RIGHT_OPEN: 3
};

/* farm cell size categories (ex: BIG = (cell height >= 40%)) */
export const CELL_SIZES = {BIG: 40, SMALL: 25, XS: 0};

/* Printer status used in automatic mode (status in influxdb) */
export const SMART_PRINTER_STATUS = {
    STAND_BY: {id: 0, value: 'Disponible', color: LED_COLORS.GREEN.color, icon: 'check_circle'},
    OFF: {id: 2, value: 'Off', color: '#b0b0b0', icon: 'block'},
    ON: {id: 3, value: 'On', color: LED_COLORS.GREEN.color, icon: 'check_circle'},
    WORKING: {id: 1, value: 'En impression', color: LED_COLORS.RED.color, icon: 'autorenew'},
    WARMING_UP: {id: 4, value: 'Pré-chauffage', color: LED_COLORS.ORANGE.color, icon: 'pending'},
    PRINTING: {id: 5, value: 'En impression', color: LED_COLORS.RED.color, icon: 'autorenew'}
};

/* Printer status used in manual mode (status in gantt) */
export const MANUAL_PRINTERS_STATUS = {
    FREE: {id: 1, value: 'Disponible', color: LED_COLORS.GREEN.color, icon: 'check_circle'},
    UPCOMING: {id: 2, value: 'En approche', color: LED_COLORS.ORANGE.color, icon: 'pending'},
    WORKING: {id: 3, value: 'En impression', color: LED_COLORS.RED.color, icon: 'autorenew'}
};

/* Unknown printer status (manual / automatic modes) */
export const UNKNOWN_PRINTER_STATUS = {id: -1, value: 'unknown', color: '#e5e5e5', icon: 'help'};

/* Material cells dehumidifier status */
export const MATERIAL_CELL_STATUS = {NO_DATA: -1, INACTIVE: 0, SWIRL: 1, REGENERATION: 2};

/* chart specs for farm dialog graphs */
export const CHARTS_DATA_RANGE = {DAY: 0, WEEK: 1, MONTH: 2, CUSTOM: 3};
export const CHARTS_UNIT_PERIODS = {HOUR: '1h', QUARTER_DAY: '6h', DAY: '1d', WEEK: '1w'}

/* temperature management states */
export const TEMPERATURE_STATES = {READY: 0, HEATING: 1, COOLING: 2};

/* max selectable temperature in smart farm */
export const TEMPERATURE_MAX = 50;

/* smart farm data fields used in influxdb */
export const SMART_FARM_DATA_FIELDS = {
    // Cell environment data
    TEMPERATURE: 'temperature',
    HUMIDITY: 'humidity',
    POLLUTION_PM1: 'pm1',
    POLLUTION_PM2_5: 'pm2_5',
    POLLUTION_PM4: 'pm4',
    POLLUTION_PM10: 'pm10',
    TVOC: 'tvoc', // SF V1
    VOC: 'voc_index', // SF V2
    POLLUTION_CO2: 'co2',
    POLLUTION_RAW_VOC: 'raw_voc_int',
    // External environment data (only SF >= V2)
    TEMPERATURE_EXT: 'temperature_sen55_ext',
    HUMIDITY_EXT: 'humidity_sen55_ext',
    POLLUTION_EXT_PM1: 'pm1_ext',
    POLLUTION_EXT_PM2_5: 'pm2_5_ext',
    POLLUTION_EXT_PM4: 'pm4_ext',
    POLLUTION_EXT_PM10: 'pm10_ext',
    VOC_EXT: 'voc_index_ext',
    // Other data
    RELAY: 'relay',
    LED_COLOR: 'led_color',
    DOOR_STATE: 'door_state',
    DEHUMIDIFIER_FLOW_FAN: 'dehumidifier_flow_fan',
    DEHUMIDIFIER_HEATER_RESISTANCE: 'dehumidifier_heater_resistance',
    AIR_EXTRACTION: 'air_extraction',
    SOUND_MODULE_ACTIVE: 'sound_module_active',
    MAINTENANCE_ACTIVE: 'maintenance_active',
    HEATER_RESISTANCE: 'heater_resistance',
    ON_OFF: 'on_off',
    // Automations
    STATE: 'state',
    PROFILE_ID: 'profile_id',
    TARGET_TEMPERATURE: 'target_temperature',
    DEHUMIDIFIER_ACTIVE: 'dehumidifier_active',
    AIR_CONDITIONER_ACTIVE: 'air_conditioner_active'
};

/* Global states */
export const STATES = {
    STANDBY: 0,
    PRODUCTION: 1,
    PRODUCTION_BEFORE_DOOR_OPEN: 2,
    PRODUCTION_DOOR_OPEN: 3,
    ERROR: 4
};

/* Pollution pm data types */
export const PM_TYPES = [
    {value: 1, label: 'pm1', field: SMART_FARM_DATA_FIELDS.POLLUTION_PM1},
    {value: 2, label: 'pm2.5', field: SMART_FARM_DATA_FIELDS.POLLUTION_PM2_5},
    {value: 3, label: 'pm4', field: SMART_FARM_DATA_FIELDS.POLLUTION_PM4},
    {value: 4, label: 'pm10', field: SMART_FARM_DATA_FIELDS.POLLUTION_PM10}
];

/* printer data fields used in influxdb */
export const PRINTER_DATA_FIELDS = {
    PRINTER_STATUS: 'printer_status'
}

/* smart farm commands names */
export const SMART_FARM_COMMANDS = {
    TEMPERATURE: 'temperature',
    HUMIDITY: 'humidity',
    AIR_EXTRACTION: 'air_extraction_command',
    RELAY_COMMAND: 'relay_command',
    LED_COLOR: 'led_command',
    OPEN_DOOR: 'open_door_command',
    ON_OFF: 'on_off_command',
    SOUND_MODULE_ACTIVE: 'sound_module_active',
    DEHUMIDIFIER_ACTIVE: 'dehumidifier_active',
    MAINTENANCE: 'maintenance_active'
};

/* smart farm command values in db */
export const FARM_EVENT_TYPES = {
    ON_OFF: 1,
    LED_COLOR: 2,
    OPEN_DOOR: 3,
    TEMPERATURE: 4,
    AIR_EXTRACTION_COMMAND: 5,
    RELAY_COMMAND: 6,
    DEHUMIDIFIER: 7,
    MAINTENANCE: 8,
    PROFILE: 9
};

export const getCellsIdFromFarmStructure = (farmStructure) => {
    let cellsId = {};
    for(const farmModule of farmStructure.farm_modules){
        for(const farmCell of farmModule.farm_cells){
            cellsId[farmCell.system_code] = farmCell.id;
        }
    }
    return cellsId;
};

/**
 * Get specific classes depending on printer size
 * @param size: number (CELL_SIZES)
 * @param baseClass: string (= base class name)
 * @returns {string}
 */
export const getClassesDependingOnCellSize = (size, baseClass) => {
    switch (size) {
        case CELL_SIZES.BIG: return baseClass + ' ' + baseClass + '-big';
        case CELL_SIZES.SMALL: return baseClass + ' ' + baseClass + '-small';
        default: return baseClass + ' ' + baseClass + '-xs';
    }
}

/**
 * Get farm element classes depending on its position in the farm structure
 * @param baseClass
 * @param isFirst
 * @param isLast
 * @returns {*}
 */
export const getFarmElementClassesWithMargins = (baseClass, isFirst, isLast) => {
    let classes = baseClass;
    if(isFirst) {
        classes += ' farm-element-first';
    }
    if(isLast) {
        classes += ' farm-element-last';
    }
    return classes;
}

/**
 * Get hexadecimal color value depending on colorType
 * @param colorType: integer (color value)
 * @returns {*|string}
 */
export const getLedHexadecimalColor = (colorType) => {
    for(let [key, value] of Object.entries(LED_COLORS)) {
        if(value.index === colorType) {
            return value.color;
        }
    }
    return LED_COLORS.OFF.color;
}

/**
 * Get material cell dehumidifier status
 * @param smartFarmCellData
 * @returns {number}
 */
export const getMaterialCellStatus = (smartFarmCellData) => {
    if(smartFarmCellData === null || smartFarmCellData === undefined)
        return MATERIAL_CELL_STATUS.NO_DATA;

    if(smartFarmCellData.dehumidifier_flow_fan === null || smartFarmCellData.dehumidifier_flow_fan === undefined ||
        smartFarmCellData.dehumidifier_heater_resistance === null || smartFarmCellData.dehumidifier_heater_resistance === undefined)
        return MATERIAL_CELL_STATUS.NO_DATA;

    if(smartFarmCellData.dehumidifier_flow_fan === 0 && smartFarmCellData.dehumidifier_heater_resistance === 0)
        return MATERIAL_CELL_STATUS.INACTIVE;

    if(smartFarmCellData.dehumidifier_flow_fan > 0 && smartFarmCellData.dehumidifier_heater_resistance === 0)
        return MATERIAL_CELL_STATUS.REGENERATION;

    if(smartFarmCellData.dehumidifier_heater_resistance > 0)
        return MATERIAL_CELL_STATUS.SWIRL;

    return MATERIAL_CELL_STATUS.NO_DATA;
}

export const getTemperatureState = (smartFarmCellData) => {
    if(smartFarmCellData === null || smartFarmCellData === undefined)
        return TEMPERATURE_STATES.READY;

    if(smartFarmCellData?.heater_resistance === 1)
        return TEMPERATURE_STATES.HEATING;

    if(smartFarmCellData?.air_conditioner_active === 1)
        return TEMPERATURE_STATES.COOLING;

    return TEMPERATURE_STATES.READY;
};

/**
 * Get an array of led colors indexes (without off index)
 * @returns {*[]}
 */
export const getLedColorsIndexesWithoutOff = () => {
    let indexes = [];
    for(let [key, value] of Object.entries(LED_COLORS)) {
        if(value.index !== LED_COLORS.OFF.index) {
            indexes.push(value.index);
        }
    }
    return indexes;
}

/**
 * Get an array of led colors hexadecimal colors (without off color)
 * @returns {*[]}
 */
export const getLedColorsColorsWithoutOff = () => {
    let colors = [];
    for(let [key, value] of Object.entries(LED_COLORS)) {
        if(value.index !== LED_COLORS.OFF.index) {
            colors.push(value.color);
        }
    }
    return colors;
}

/**
 * Get string gap between today and filter date change
 * @param filterChangeDate: number (timestamp)
 * @returns {string}
 */
export const getFilterChangeGapFromToday = (filterChangeDate) => {
    // Today at midnight
    const todayAtMidnight = (new Date()).setHours(0, 0, 0, 0);
    // filter change date at midnight
    const filterChangeAtMidnight = (new Date(filterChangeDate * 1000)).setHours(0, 0, 0, 0);

    const differenceInSeconds = filterChangeAtMidnight - todayAtMidnight;
    const absDifferenceInSeconds = Math.abs(differenceInSeconds);

    // Case : change filter date is today
    if(((new Date(filterChangeAtMidnight)).getDate() === (new Date(todayAtMidnight)).getDate()) &&
        (Math.floor(Math.abs(filterChangeAtMidnight - todayAtMidnight)) < 3600 * 24 * 1000)) {
        return 'aujourd\'hui';
    }

    // Calculate gap
    let gap;
    if(absDifferenceInSeconds < 3600 * 24 * 31 * 1000) {
        const days = (new Date(absDifferenceInSeconds)).getDate() - 1;
        gap = days + (days > 1 ? ' jours' : ' jour');
    } else if(absDifferenceInSeconds < 3600 * 24 * 365 * 1000) {
        const months = (new Date(absDifferenceInSeconds)).getMonth();
        gap = months + (months > 1 ? ' mois' : ' mois');
    } else {
        const years = (new Date(absDifferenceInSeconds)).getFullYear() - 1970;
        gap = years + (years > 1 ? ' années' : ' année');
    }

    return differenceInSeconds > 0 ? 'dans ' + gap : 'il y a ' + gap;
}

/**
 * Get printer smart printer status
 * @param printerId
 * @param printersStatus
 * @returns {*|null}
 */
export const getPrinterStatusByPrinterId = (printerId, printersStatus) => {
    if(printersStatus !== null && printersStatus !== undefined) {
        for(let printerStatus of printersStatus) {
            if(printerStatus.id === printerId) {
                return printerStatus;
            }
        }
    }
    return null;
}

/**
 * Get SMART_PRINTER_STATUS for each printer status found in influxdb formatted data, with printer id
 * @param printersData
 * @returns {*[]}, status = {id: printerId, status: SMART_PRINTER_STATUS}
 */
export const getSmartPrinterStatusFromPrintersStatusData = (printersData) => {
    let printersStatus = [];
    for(let [keyData, valueData] of Object.entries(printersData)) {
        let isStatusFound = false;
        for(let [keyStatus, valueStatus] of Object.entries(SMART_PRINTER_STATUS)) {
            if(valueStatus.id === valueData.printer_status) {
                isStatusFound = true;
                printersStatus.push({id: parseInt(keyData), status: valueStatus});
            }
        }
        // Case : status not existing in app
        if(!isStatusFound) {
            printersStatus.push({id: parseInt(keyData), status: UNKNOWN_PRINTER_STATUS})
        }
    }
    return printersStatus;
}

/**
 * Get SMART_PRINTER_STATUS for each printer status found in influxdb formatted data
 * @param printerStatus
 * @returns {*}: PRINTER_STATUS value
 */
export const getManualPrinterStatus = (printerStatus) => {
    // Case : app is in minimal app mode
    if(process.env.REACT_APP_MINIMAL_APP === 'true') {
        return UNKNOWN_PRINTER_STATUS;
    }
    // Check gantt printer status
    if(printerStatus !== undefined && printerStatus !== null) {
        for(let [key, value] of Object.entries(MANUAL_PRINTERS_STATUS)) {
            if(value.value === printerStatus) {
                return value;
            }
        }
    }
    return UNKNOWN_PRINTER_STATUS;
}

/**
 * Merge environment data into one array with header row
 * @param temperatureList
 * @param humidityList
 * @param tvocList
 * @param pollutionList
 * @param vocType
 * @param pollutionLabel
 * @returns {string[][]}
 */
export const mergeEnvironmentDataLists = (temperatureList, humidityList) => {
    // Initialize formatted data with temperature list
    let formattedData = [['date', 'temperature']];
    temperatureList.map(temperatureItem => {
        formattedData.push(temperatureItem);
    });
    // Add other lists data
    formattedData = addListToMergeEnvironmentList('humidity', humidityList, formattedData);

    // Format date
    for(let i = 1; i < formattedData.length; i++) {
        formattedData[i][0] = getFormattedFullFrDateWithSeconds(Math.floor(formattedData[i][0] / 1000));
    }

    return formattedData;
}

/**
 * Add list data to merge list (dates column have to be equivalent)
 * @param listName
 * @param listToAdd
 * @param mergeList
 * @returns {*}
 */
const addListToMergeEnvironmentList = (listName, listToAdd, mergeList) => {
    mergeList[0].push(listName);
    listToAdd.map(item => {
        for(let row of mergeList) {
            if(row[0] === item[0]) {
                row.push(item[1]);
                break;
            }
        }
    });
    return mergeList;
}

export const mergeData = (oldData, newData) => {
    const mergedData = structuredClone(oldData);
    if(mergedData === null) return {};

    for (const systemCode in newData) {
        if (!mergedData.hasOwnProperty(systemCode))
            mergedData[systemCode] = {};

        for (const key in newData[systemCode])
            mergedData[systemCode][key] = newData[systemCode][key];

        mergedData[systemCode].time = getCurrentTimestamp() * 1000;
    }

    return mergedData;
};

/**
 * Create csv data from array
 * @param arrayData
 * @param separator
 * @returns {string}
 */
export const createCsvData = (arrayData, separator = ',') => {
    return 'data:text/csv;charset=utf-8,' + arrayData.map(row => row.join(separator)).join("\n");
}

/**
 * Download csv file from csv formatted data
 * @param csvData
 * @param startFilename
 * @returns {Promise<void>}
 */
export const downloadCsvData = (csvData, startFilename = 'environment_data') => {
    try {
        // Create file name with current date and organization
        const separator = '_';
        const date = getCustomFormattedDateBeginningByYear(getCurrentTimestamp(), separator);
        const organizationName = getCurrentUser().organization.name.toLowerCase().replaceAll(' ', separator);
        const fileName = [startFilename, organizationName, date].join(separator);

        // Download data
        const encodedUri = encodeURI(csvData);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        // Remove download link
        document.body.removeChild(link);

        return Promise.resolve();
    } catch {
        return Promise.reject('error while creating csv');
    }
}

/**
 * Get all printers ids in farm structure
 * @param farmStructure
 * @returns {*[]}
 */
export const getFarmStructurePrintersIds = (farmStructure) => {
    let printersIds = [];
    if(farmStructure !== null && farmStructure.farm_modules) {
        farmStructure.farm_modules.map(module => {
           if(module.farm_cells) {
               module.farm_cells.map(cell => {
                  if(cell.printers) {
                      cell.printers.map(printer => printersIds.push(printer.id));
                  }
               });
           }
        });
    }
    return printersIds;
}

/**
 * Get material status label depending on material status (index)
 * @param materialStatus
 * @returns {string}
 */
export const getMaterialStatusLabel = (materialStatus) => {
    switch (materialStatus) {
        case MATERIAL_CELL_STATUS.NO_DATA: return 'Eteint';
        case MATERIAL_CELL_STATUS.INACTIVE: return 'Inactif';
        case MATERIAL_CELL_STATUS.SWIRL: return 'Régénération';
        case MATERIAL_CELL_STATUS.REGENERATION: return 'Brassage de l\'air';
        default: return 'Off';
    }
}

/**
 * Get smart farm main version number depending on complete smart farm version number
 * @param version (ex: 1.0.0)
 * @returns {number}
 */
export const getFarmVersion = (version) => {
    if(version.charAt(0) === '1' || version.charAt(0) === '2') {
        return parseInt(version.charAt(0));
    }
    return 2;
}

/**
 * get an array of environment data influxdb fields depending on smart farm version and system code type (cell / structure)
 * @param isCellData
 * @param farmVersion
 * @returns {{tvoc: string|null, temperature: string|null, humidity: string|null}}
 */
export const getInfluxdbEnvironmentDataFieldsWithoutPollution = (isCellData, farmVersion) => {
    if(farmVersion === 2) {
        if(isCellData) {
            return {temperature: SMART_FARM_DATA_FIELDS.TEMPERATURE, humidity: SMART_FARM_DATA_FIELDS.HUMIDITY, tvoc: SMART_FARM_DATA_FIELDS.VOC};
        } else {
            return {temperature: SMART_FARM_DATA_FIELDS.TEMPERATURE_EXT, humidity: SMART_FARM_DATA_FIELDS.HUMIDITY_EXT, tvoc: SMART_FARM_DATA_FIELDS.VOC_EXT};
        }
    } else if(isCellData) {
        return {temperature: SMART_FARM_DATA_FIELDS.TEMPERATURE, humidity: SMART_FARM_DATA_FIELDS.HUMIDITY, tvoc: SMART_FARM_DATA_FIELDS.TVOC};
    }
    return {temperature: null, humidity: null, tvoc: null};
}

/**
 * Check if all system code sound module are inactive
 * @param smartFarmData
 * @returns {boolean}
 */
export const areAllSoundModulesInactive = (smartFarmData) => {
    if(smartFarmData) {
        for(let [key, value] of Object.entries(smartFarmData)) {
            if(value[SMART_FARM_DATA_FIELDS.SOUND_MODULE_ACTIVE]) {
                return false;
            }
        }
        return true;
    }
    return false;
}

/**
 * Activate a farm profile in a specific farm cell
 * @param profile
 * @param systemCode
 * @param farmCellId
 * @param isAutomatic
 * @returns {Promise<void>}
 */
export const activateFarmProfile = (profile, systemCode, farmCellId, isAutomatic = false) => {
    let promises = [];
    // Temperature
    if(profile.temperature !== undefined && profile.temperature !== null) {
        sendFarmCommandEvent(systemCode, SMART_FARM_COMMANDS.TEMPERATURE, profile.temperature);
        promises.push(postFarmEvent(farmCellId, FARM_EVENT_TYPES.TEMPERATURE, profile.temperature, null, isAutomatic));
    }
    return Promise.all(promises);
}

/**
 * Stop any farm profile running in a specific farm cell
 * @param systemCode
 * @param farmCellId
 * @param isAutomatic
 * @returns {Promise<Awaited<unknown>[]>}
 */
export const stopFarmProfile = (systemCode, farmCellId, isAutomatic = false) => {
    let promises = [];
    // Temperature
    sendFarmCommandEvent(systemCode, SMART_FARM_COMMANDS.TEMPERATURE, -1);
    promises.push(postFarmEvent(farmCellId, FARM_EVENT_TYPES.TEMPERATURE, -1, null, isAutomatic));
    return Promise.all(promises);
}

/**
 * Check if smart farm is in general maintenance from smart farm data (= if all cells are in maintenance)
 * @param smartFarmData
 * @returns {{date: number, isInMaintenance: boolean}}
 */
export const getSmartFarmDataGeneralMaintenance = (smartFarmData) => {
    let isGeneralMaintenance = true;
    let date = 0;
    if(smartFarmData) {
        for(let [key, cell] of Object.entries(smartFarmData)) {
            if(date === 0  && cell.time) {
                date = Math.floor(cell.time / 1000);
            }
            if(!cell[SMART_FARM_DATA_FIELDS.MAINTENANCE_ACTIVE]) {
                isGeneralMaintenance = false;
                break;
            }
        }
    }
    return {date: date, isInMaintenance: isGeneralMaintenance};
}

/**
 * Check if a maintenance from bdd is running (= no end date)
 * @param maintenance
 * @returns {boolean}
 */
export const isMaintenanceRunning = (maintenance) => {
    return maintenance && !maintenance.end_date;
}

/**
 * Check if maintenance saved in database is corresponding to maintenance data received from smart farm
 * @param savedMaintenance
 * @param farmDataMaintenance
 * @returns {boolean|null}
 */
export const isSavedMaintenanceConfirmedBySmartFarmData = (savedMaintenance, farmDataMaintenance) => {
    if(savedMaintenance && farmDataMaintenance && farmDataMaintenance.date && farmDataMaintenance.isInMaintenance !== null) {
        const isInMaintenance = isMaintenanceRunning(savedMaintenance);
        if((isInMaintenance && !farmDataMaintenance.isInMaintenance && Math.abs(savedMaintenance.start_date - farmDataMaintenance.date) < COMMAND_TIMEOUT_LIMIT) ||
            (!isInMaintenance && farmDataMaintenance.isInMaintenance &&savedMaintenance.end_date && Math.abs(savedMaintenance.end_date - farmDataMaintenance.date) < COMMAND_TIMEOUT_LIMIT)) {
            return null;
        }
        if((isInMaintenance && farmDataMaintenance.isInMaintenance) ||
            (!isInMaintenance && !farmDataMaintenance.isInMaintenance)) {
            return true;
        }
        return false;
    }
    return null;
}
